import {
  format,
  parse,
  parseISO,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
  add,
  sub,
  isAfter,
  isValid,
  formatDistanceToNow,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import rangeRight from 'lodash/rangeRight';
import { intervalsHash } from '@constants';

/**
 * Utility function to convert a ISO string date to a string in the following format
 * Month / Date / Year
 * i.e: 12 / 03 / 2020
 */
export function convertISODateToRegularDateString(ISOString: string): string {
  if (!ISOString) return '';

  return new Date(ISOString).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Generate an array of string date from startDate to endDate
 * @param startDate - Date Object
 * @param endDate - Date Object
 * @param interval - string, one of [day,year,month,week]
 * @returns {Array<string>} - i.e: [01/01/2021, 01/08/2021, 01/15/2021] etc...
 */
export const generateAllDatesWithinInterval = (
  startDate: Date,
  endDate: Date,
  interval: string,
): Array<string> => {
  // Convert to UTC
  const fromDate = toZonedTime(startDate, 'UTC');
  const toDate = toZonedTime(endDate, 'UTC');

  // Calculate difference based on interval
  let diff: number;
  switch (interval.toLowerCase()) {
    case 'day':
      diff = differenceInDays(toDate, fromDate);
      break;
    case 'week':
      diff = differenceInWeeks(toDate, fromDate);
      break;
    case 'month':
      diff = differenceInMonths(toDate, fromDate);
      break;
    case 'year':
      diff = differenceInYears(toDate, fromDate);
      break;
    default:
      diff = differenceInDays(toDate, fromDate);
  }

  const dateArray: string[] = [];
  for (let i = 0; i <= diff; i++) {
    const currentDate = add(fromDate, { [interval.toLowerCase() + 's']: i });
    dateArray.push(format(currentDate, 'MM/dd/yyyy'));
  }
  return dateArray;
};

/**
 * Generate a date string in the format of 'YYYY-MM-DD' based on the interval (day, week, month)
 * @param interval - One of ['Day', 'Week', 'Month']. If 'day', returns today's date
 * @returns {string}
 */
export const generateDateBeforeToday = (
  interval: string,
  count: number,
  formatStr = 'yyyy-MM-dd',
): string => {
  const lowerCaseInterval = interval.toLowerCase();
  const now = toZonedTime(new Date(), 'UTC');

  if (count === 0) return format(now, formatStr); // Today

  const pastDate = sub(now, { [lowerCaseInterval + 's']: count });
  return format(pastDate, formatStr);
};

/**
 * If the dateString given is falsy, returns an empty string
 * If is returns the locale version
 * @param dateString
 * @returns
 */
export const falsyCheckDate = (dateString: Nullable<string> | undefined) =>
  dateString ? new Date(dateString).toLocaleString() : '';

/**
 * Generates a start date given an interval
 * For instance, selectedInterval = "Last Day", and today is "09/27/2021", generates "09/20/2021"
 * @param selectedInterval - One of day, month, year
 * @returns
 */
export const getStartDate = (selectedInterval: string, format?: string) => {
  const subtractCount = intervalsHash[selectedInterval].value;

  return generateDateBeforeToday('day', subtractCount, format);
};

/**
 * Take an array of date time string and returns the string
 * that is considered furthest in time
 * @param dateArray
 * @returns
 */
export const calculateLatestDate = (dateArray: Array<string>): string => {
  if (dateArray.length === 1) return dateArray[0];

  let latestDate = dateArray[0];

  for (let i = 1; i < dateArray.length; i++) {
    const currentDate = dateArray[i];
    const currDate = parseISO(currentDate);
    const latestDateParsed = parseISO(latestDate);

    if (isAfter(currDate, latestDateParsed)) {
      latestDate = currentDate;
    }
  }
  return latestDate;
};

/**
 *  @param nativeDate: In the format of 'YYYY-MM-DD'
 *  @param expectedFormat: any date-fns format string
 *  @returns {string}: Time string in expectedFormat
 *
 */
export const convertNativeDateToExpectedFormat = (
  nativeDate: string,
  expectedFormat = 'MM.dd.yyyy',
) => {
  const date = parse(nativeDate, 'yyyy-MM-dd', new Date());
  return format(date, expectedFormat);
};

/**
 *  @param range: Number of prvious months
 *  @param endMonth: In the format of 'MM/YYYY'
 *  @returns {Array<string>}: From earliest to latest, example: ['Oct 2021', 'Nov 2021', 'Dec 2021']
 *
 */
export const getSelectedMonthsArray = (range: number, endMonth: string) => {
  return rangeRight(range).map((n) => {
    const date = parse(endMonth, 'MM-yyyy', new Date());
    const pastDate = sub(date, { months: n });
    return format(pastDate, 'MMM yyyy');
  });
};

/**
 *  @param monthArray: An array of month-year string from earliest to latest, example: ['Oct 2021', 'Nov 2021', 'Dec 2021']
 *  @returns {Array<string>}: example: [{month: 'Oct', year: 2021}]
 *
 */
export const getMonthAndYearArray = (monthArray: Array<string>) => {
  return monthArray.map((n) => {
    const [month, year] = n.split(' ');
    return {
      month,
      year,
    };
  });
};

type MonthAndYear = {
  month: string;
  year: string;
};

type TeamUtilization = {
  [index: number]: {
    [index: string]: number;
  };
};

/**
 *  @param monthAndYearHashArray: An array of MonthAndYearHash from earliest to latest, example: [{month:'Oct',year:2021},{month: 'Nov',year:2021},{month: 'Dec',year:2021}]
 *  @returns {Array<number>}: percentage number [0,3.83,8.87, 9.22]
 *
 */
export const getUtilizationDataByMonth = (
  monthAndYearHashArray: Array<MonthAndYear>,
  teamUtilizationData: TeamUtilization,
) => {
  return monthAndYearHashArray.map((monthYearObj) => {
    const yearKey = +monthYearObj.year;
    const monthKey = monthYearObj.month;
    const utilizationPercentage =
      (teamUtilizationData?.[yearKey]?.[monthKey] * 100).toFixed(2) || 0;
    return +utilizationPercentage;
  });
};

type utilizationByMonth = {
  [index: string]: number;
};

/**
 *  @param utiliazationMonthData: {Jan: 0, Feb: 0, Mar: 0, Apr: 0...}
 *  @returns {Number}: month in number form
 *
 */
export const getFirstNonZeroUtilizationMonth = (utiliazationMonthData: utilizationByMonth) => {
  const monthArray = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  for (let i = 0; i < monthArray.length; i++) {
    const monthStr = monthArray[i];
    if (utiliazationMonthData[monthStr]) {
      return i + 1;
    }
  }
  return null;
};

type MinAndMaxMonthAndYear = {
  min: {
    year: number;
    month: number;
  };
  max: {
    year: number;
    month: number;
  };
};
/**
 *  @param yearArray: ['2017', '2018', '2019', '2020', '2021', '2022']
 *  @param teamUtilizationYearData: {2017: {Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, …}, 2018: {Jan: 0, Feb: 0, …}}
 * @returns {Object} Example: {min: {year: 2018, month: 9}, max: {year: 2022, month: 5}}
 *
 */
export const getFirstNonZeroUtilizationYearAndMonth = (
  yearArray: Array<string>,
  teamUtilizationYearData: TeamUtilization,
): MinAndMaxMonthAndYear => {
  const now = new Date();
  const currentYear = +format(now, 'yyyy');
  const currentMonth = +format(now, 'M');

  for (let i = 0; i < yearArray.length; i++) {
    const yearNum = +yearArray[i];
    const monthNum = getFirstNonZeroUtilizationMonth(teamUtilizationYearData[yearNum]);

    if (monthNum) {
      const yearAndMonth = {
        min: { year: yearNum, month: monthNum },
        max: { year: currentYear, month: currentMonth },
      };
      return yearAndMonth;
    }
  }
  return {
    min: { year: currentYear, month: 1 },
    max: { year: currentYear, month: currentMonth },
  };
};

/**
 *  @param monthAndDateStr: {month:'11' , date: '25'}
 *  @returns {Number}: with 2 decimals
 *
 */
export const getDateDecimal = (monthAndDateStr: {
  monthLabel: string;
  month: string;
  date: string;
}) => {
  const dateNum = +monthAndDateStr.date;
  const monthNum = +monthAndDateStr.month;
  let decimal;
  switch (monthNum) {
    case 1:
    case 3:
    case 5:
    case 7:
    case 8:
    case 10:
    case 12:
      if (dateNum === 30) {
        decimal = 0.99;
      } else {
        decimal = +(dateNum / 31).toFixed(2);
      }
      break;
    case 4:
    case 6:
    case 9:
    case 11:
      if (dateNum === 30) {
        decimal = 0.99;
      } else {
        decimal = +(dateNum / 30).toFixed(2);
      }
      break;
    case 2:
      if (dateNum === 29) {
        decimal = 1;
      } else {
        decimal = +(dateNum / 28).toFixed(2);
      }
      break;
    default:
      decimal = 0;
  }
  return decimal;
};

export const formatDate = (dateStr: Nullable<string> | undefined, formatStr = 'yyyy-MM-dd') => {
  if (!dateStr) return '';

  const date = toZonedTime(parseISO(dateStr), 'UTC');
  return format(date, formatStr);
};

/** "3 minutes ago" for an ISO timestamp; '' when missing or unparseable. */
export const formatRelativeTime = (dateStr: Nullable<string> | undefined) => {
  if (!dateStr) return '';
  const date = parseISO(dateStr);
  if (!isValid(date)) return '';
  return formatDistanceToNow(date, { addSuffix: true });
};
